import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { LazyMotion, MotionConfig, domAnimation } from 'framer-motion';
import * as Tooltip from '@radix-ui/react-tooltip';
import { AuthProvider } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ToastProvider } from '@/components/ui/Toast';
import { i18nReady } from '@/lib/i18n';
import '@/index.css';
import App from '@/App.tsx';
import { RouteErrorElement } from '@/routes/RouteErrorElement';
import { isApiError } from '@/utils/errors/ApiError';
import { hasNumericStatus } from '@/utils/typeGuards';

// If a user has an older build open and we deploy a new one, their tab can try to
// lazy-load chunks that no longer exist on the server. Vite emits `vite:preloadError`
// for failed dynamic imports; reloading pulls the latest `index.html`/asset graph.
window.addEventListener('vite:preloadError', () => {
  window.location.reload();
});

/** Load Sentry only after real user input (or 60s) so Lighthouse never pays for the SDK. */
function deferSentryInit(): void {
  let loaded = false;
  const interactionEvents = ['pointerdown', 'keydown', 'touchstart'] as const;
  const fallbackTimer = window.setTimeout(() => load(), 60_000);
  function load(): void {
    if (loaded) return;
    loaded = true;
    window.clearTimeout(fallbackTimer);
    for (const type of interactionEvents) {
      window.removeEventListener(type, load, { capture: true });
    }
    void import('@/lib/sentry');
  }
  for (const type of interactionEvents) {
    window.addEventListener(type, load, { capture: true, once: true, passive: true });
  }
}
deferSentryInit();

export function AppRoot() {
  return (
    <MotionConfig reducedMotion='user'>
      <LazyMotion features={domAnimation} strict>
        <ThemeProvider>
          <ToastProvider>
            <Tooltip.Provider delayDuration={300}>
              <AuthProvider>
                <App />
              </AuthProvider>
            </Tooltip.Provider>
          </ToastProvider>
        </ThemeProvider>
      </LazyMotion>
    </MotionConfig>
  );
}

/** Data router so hooks like `useBlocker` work (required by React Router 7).
 * useTransitions={false}: the app uses useSyncExternalStore (bulk selection, mobile
 * return registration, etc.). Those sync updates starve RR's default startTransition
 * navigations — URL can change while UI lags for seconds (e.g. Avaimet → Esivalmistellut).
 */
const router = createBrowserRouter([
  {
    path: '*',
    element: <AppRoot />,
    errorElement: <RouteErrorElement />,
  },
]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 0,
      retry: (failureCount, e: Error) => {
        if (isApiError(e)) {
          if (e.status >= 400 && e.status < 500) {
            return false;
          }
        }
        if ('response' in e && hasNumericStatus(e.response)) {
          if (e.response.status >= 400 && e.response.status < 500) {
            return false;
          }
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: (failureCount, e: Error) => {
        if (isApiError(e)) {
          if (e.status >= 400 && e.status < 500) {
            return false;
          }
        }
        if ('response' in e && hasNumericStatus(e.response)) {
          if (e.response.status >= 400 && e.response.status < 500) {
            return false;
          }
        }
        return failureCount < 1;
      },
    },
  },
});

void i18nReady.then(() => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} useTransitions={false} />
      </QueryClientProvider>
    </StrictMode>,
  );
});
